import React, {
  useEffect,
  useMemo,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { fetchMenu } from "../../actions/menuActions";

import { useCart } from "../../components/CartContext";

import "./css/HomeScreen.css";


// =====================================================
// THEME 7 STATIC INTERNET IMAGES
// =====================================================

const THEME7_IMAGES = {

  // ===================================================
  // MAIN HERO
  // ===================================================

  hero:
    "https://bluelagoon.co.in/assets/images/tandoori-chicken.png",


  // ===================================================
  // DINING / RESTAURANT LIFESTYLE BANNER
  // ===================================================

  diningBanner:
    "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1800&q=85",


  // ===================================================
  // GALLERY
  // ===================================================

  gallery: [

    {
      id: 1,

      image:
        "https://images.restaurantfurniture.net/dpr_1.0%2Cf_auto%2Cq_auto%2Cw_650/rfnet/media/wysiwyg/guides/234/101_accent_lighing.png",

      alt:
        "Warm Indian restaurant interior",
    },

    {
      id: 2,

      image:
        "https://i0.wp.com/kottaramrestaurant.com/wp-content/uploads/2023/06/Kottaram4.jpg?fit=571%2C571&ssl=1",

      alt:
        "Traditional Indian food",
    },

    {
      id: 3,

      image:
        "https://b.zmtcdn.com/data/pictures/6/18573016/2fadd5c8d05c6f70dad90c480eabdee2.jpg",

      alt:
        "Indian restaurant interior",
    },

    {
      id: 4,

      image:
        "https://www.hello-hyderabad.com/public/assets/images/hello-hyderabad-dining-table.png",

      alt:
        "Indian dining experience",
    },

  ],

};


// =====================================================
// HOME SCREEN
// =====================================================

function HomeScreen() {

  const dispatch = useDispatch();

  const navigate = useNavigate();


  // ===================================================
  // URL PARAMETERS
  // ===================================================

  const {
    restaurantId,
    tableId,
  } = useParams();


  // ===================================================
  // CART
  // ===================================================

  const {
    addToCart,
    cartCount,
    cartTotal,
  } = useCart();


  // ===================================================
  // REDUX MENU
  // ===================================================

  const menuState = useSelector(
    (state) => state.menu
  );


  const themeState = useSelector(
    (state) => state.theme
  );


  // ===================================================
  // RESTAURANT NAME
  // ===================================================

  const restaurantName =
    themeState?.restaurantName ||
    menuState?.restaurantName ||
    "Spice & Saffron";


  // ===================================================
  // CATEGORIES
  // ===================================================

  const categoriesFromRedux =
    menuState?.categories || [];


  // ===================================================
  // FETCH MENU
  // ===================================================

  useEffect(() => {

    if (!restaurantId) {
      return;
    }

    dispatch(
      fetchMenu(
        restaurantId
      )
    );

  }, [
    dispatch,
    restaurantId,
  ]);


  // ===================================================
  // SORT CATEGORIES AND ITEMS
  // ===================================================

  const categories = useMemo(() => {

    if (
      !Array.isArray(
        categoriesFromRedux
      )
    ) {
      return [];
    }


    return [
      ...categoriesFromRedux,
    ]

      .filter(
        (category) =>
          category &&
          category.is_active !== false
      )

      .sort(
        (a, b) =>
          Number(
            a.display_order || 0
          ) -
          Number(
            b.display_order || 0
          )
      )

      .map((category) => {

        const items =
          Array.isArray(
            category.items
          )
            ? category.items
            : [];


        return {

          ...category,

          items: [
            ...items,
          ]

            .filter(
              (item) =>
                item &&
                item.is_available !== false
            )

            .sort(
              (a, b) =>
                Number(
                  a.display_order || 0
                ) -
                Number(
                  b.display_order || 0
                )
            ),

        };

      });

  }, [
    categoriesFromRedux,
  ]);


  // ===================================================
  // CUSTOMER FAVOURITES
  //
  // DISPLAY ORDER 1 AND 2
  // FROM EVERY CATEGORY
  // ===================================================

  const favouriteItems = useMemo(() => {

    const favourites = [];


    categories.forEach(
      (category) => {

        const firstTwo =
          category.items.filter(
            (item) => {

              const order =
                Number(
                  item.display_order || 0
                );

              return (
                order === 1 ||
                order === 2
              );

            }
          );


        firstTwo.forEach(
          (item) => {

            favourites.push({

              ...item,

              categoryName:
                category.name,

              categoryId:
                category.id,

            });

          }
        );

      }
    );


    return favourites;

  }, [
    categories,
  ]);


  // ===================================================
  // EXPLORE CATEGORIES
  // ===================================================

  const exploreCategories =
    useMemo(() => {

      return categories.slice(
        0,
        5
      );

    }, [
      categories,
    ]);


  // ===================================================
  // SCROLL TO MENU
  // ===================================================

  const scrollToMenu = () => {

    const element =
      document.getElementById(
        "t7-menu"
      );


    if (element) {

      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

    }

  };


  // ===================================================
  // SCROLL TO CATEGORY
  // ===================================================

  const scrollToCategory = (
    categoryId
  ) => {

    const element =
      document.getElementById(
        `t7-category-${categoryId}`
      );


    if (element) {

      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

    }

  };


  // ===================================================
  // SCROLL TO SECTION
  // ===================================================

  const scrollToSection = (
    sectionId
  ) => {

    const element =
      document.getElementById(
        sectionId
      );


    if (element) {

      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

    }

  };


  // ===================================================
  // ADD TO CART
  // ===================================================

  const handleAddToCart = (
    item
  ) => {

    addToCart(item);

  };


  // ===================================================
  // OPEN CART
  // ===================================================

  const openCart = () => {

    navigate(
      `/${restaurantId}/table/${tableId}/cart`
    );

  };


  // ===================================================
  // LOADING
  // ===================================================

  if (menuState?.loading) {

    return (

      <div className="t7-loading">

        <div className="t7-loading-spinner"></div>

        <p>
          Preparing your table...
        </p>

      </div>

    );

  }


  // ===================================================
  // ERROR
  // ===================================================

  if (menuState?.error) {

    return (

      <div className="t7-error">

        <div className="t7-error-card">

          <div className="t7-error-icon">
            !
          </div>


          <h2>
            Unable to load menu
          </h2>


          <p>

            {typeof menuState.error === "string"
              ? menuState.error
              : "Something went wrong while loading the menu."}

          </p>


          <button
            type="button"
            onClick={() =>
              dispatch(
                fetchMenu(
                  restaurantId
                )
              )
            }
          >
            Try Again
          </button>

        </div>

      </div>

    );

  }


  return (

    <main className="t7-page">


      {/* =================================================
          MAIN HERO
      ================================================= */}

      <section className="t7-hero">

        <div className="t7-hero-image">

          <img
            src={THEME7_IMAGES.hero}
            alt="Tandoori Chicken"
          />

        </div>


        <div className="t7-hero-overlay"></div>


        <div className="t7-hero-content">

          <span className="t7-hero-label">
            SIGNATURE DISH
          </span>


          <h1>
            Tandoori
            <br />
            Chicken
          </h1>


          <p>
            Smoky. Juicy. Unforgettable.
          </p>


          <button
            type="button"
            className="t7-orange-button"
            onClick={scrollToMenu}
          >
            Order Now

            <span>
              →
            </span>

          </button>

        </div>


        <div className="t7-hero-dots">

          <span className="active"></span>

          <span></span>

          <span></span>

          <span></span>

        </div>

      </section>



      {/* =================================================
          SERVICES
          DELIVERY REMOVED
      ================================================= */}

      <section className="t7-services">

        <div className="t7-services-grid">


          {/* DINE IN */}

          <button
            type="button"
            className="t7-service-card"
            onClick={scrollToMenu}
          >

            <div className="t7-service-icon">
              🍴
            </div>

            <span>
              Dine In
            </span>

          </button>



          {/* TAKEAWAY */}

          <button
            type="button"
            className="t7-service-card"
            onClick={scrollToMenu}
          >

            <div className="t7-service-icon">
              🛍
            </div>

            <span>
              Takeaway
            </span>

          </button>



          {/* CATERING */}

          <button
            type="button"
            className="t7-service-card"
            onClick={() =>
              scrollToSection(
                "t7-catering"
              )
            }
          >

            <div className="t7-service-icon">
              ♨
            </div>

            <span>
              Catering
            </span>

          </button>


        </div>

      </section>



      {/* =================================================
          EXPLORE OUR MENU
      ================================================= */}

      <section className="t7-explore">

        <div className="t7-section-container">


          <div className="t7-section-heading">

            <div>

              <span className="t7-small-label">
                DISCOVER
              </span>


              <h2>
                Explore Our Menu
              </h2>

            </div>


            <button
              type="button"
              className="t7-view-all"
              onClick={scrollToMenu}
            >

              View All

              <span>
                →
              </span>

            </button>

          </div>



          <div className="t7-category-cards">

            {exploreCategories.map(
              (category) => {

                const image =
                  category.image ||
                  category.items?.[0]?.image;


                return (

                  <button
                    type="button"
                    className="t7-category-card"
                    key={category.id}
                    onClick={() =>
                      scrollToCategory(
                        category.id
                      )
                    }
                  >

                    <div className="t7-category-circle">

                      {image ? (

                        <img
                          src={image}
                          alt={category.name}
                        />

                      ) : (

                        <div className="t7-category-placeholder">
                          ✦
                        </div>

                      )}

                    </div>


                    <span>
                      {category.name}
                    </span>

                  </button>

                );

              }
            )}

          </div>

        </div>

      </section>



      {/* =================================================
          RESTAURANT DINING BANNER
          REPLACED CHEF'S SPECIAL
      ================================================= */}

      <section className="t7-dining-banner">

        <div className="t7-dining-image">

          <img
            src={
              THEME7_IMAGES.diningBanner
            }
            alt="Guests enjoying dinner together in a restaurant"
            loading="lazy"
          />

        </div>


        <div className="t7-dining-overlay"></div>


        <div className="t7-dining-content">

          <span className="t7-dining-label">
            THE SPICE &amp; SAFFRON EXPERIENCE
          </span>


          <h2>
            Good Food.
            <br />
            Good Company.
          </h2>


          <p>
            Come together, share a table,
            and enjoy the flavours of India.
          </p>


          <button
            type="button"
            className="t7-orange-button"
            onClick={scrollToMenu}
          >

            Explore Our Menu

            <span>
              →
            </span>

          </button>

        </div>

      </section>



      {/* =================================================
          WHY DINE WITH US
      ================================================= */}

      <section
        id="t7-why"
        className="t7-why"
      >

        <div className="t7-section-container">


          <div className="t7-centered-heading">

            <span className="t7-small-label">
              OUR PROMISE
            </span>


            <h2>
              Why Dine With Us
            </h2>


            <div className="t7-heading-ornament">
              ❋
            </div>

          </div>



          <div className="t7-why-grid">


            <div className="t7-why-card">

              <div className="t7-why-icon">
                ◉
              </div>

              <h3>
                Authentic
                <br />
                Recipes
              </h3>

            </div>



            <div className="t7-why-card">

              <div className="t7-why-icon">
                ❧
              </div>

              <h3>
                Fresh
                <br />
                Ingredients
              </h3>

            </div>



            <div className="t7-why-card">

              <div className="t7-why-icon">
                ♨
              </div>

              <h3>
                Hygienic
                <br />
                Kitchen
              </h3>

            </div>



            <div className="t7-why-card">

              <div className="t7-why-icon">
                ◉
              </div>

              <h3>
                Memorable
                <br />
                Experience
              </h3>

            </div>


          </div>

        </div>

      </section>



      {/* =================================================
          GALLERY
      ================================================= */}

      <section
        id="t7-gallery"
        className="t7-gallery"
      >

        <div className="t7-section-container">


          <div className="t7-section-heading">

            <div>

              <span className="t7-small-label">
                MOMENTS
              </span>


              <h2>
                Gallery
              </h2>

            </div>


            <button
              type="button"
              className="t7-view-all"
            >

              View All

              <span>
                →
              </span>

            </button>

          </div>



          <div className="t7-gallery-grid">

            {THEME7_IMAGES.gallery.map(
              (
                galleryItem,
                index
              ) => (

                <div
                  className={
                    index === 0
                      ? "t7-gallery-item t7-gallery-large"
                      : "t7-gallery-item"
                  }
                  key={
                    galleryItem.id
                  }
                >

                  <img
                    src={
                      galleryItem.image
                    }
                    alt={
                      galleryItem.alt
                    }
                    loading="lazy"
                  />

                </div>

              )
            )}

          </div>

        </div>

      </section>



      {/* =================================================
          CATERING
      ================================================= */}

      <section
        id="t7-catering"
        className="t7-catering"
      >

        <div className="t7-catering-content">

          <span className="t7-small-label">
            CELEBRATE WITH US
          </span>


          <h2>

            Bring India to

            <br />

            <span>
              Your Event
            </span>

          </h2>


          <p>
            Authentic catering for all occasions.
          </p>


          <button
            type="button"
            className="t7-orange-button"
            onClick={scrollToMenu}
          >

            Request Catering

            <span>
              →
            </span>

          </button>

        </div>


        <div className="t7-catering-decoration">

          <div className="t7-catering-dome">
            ◇
          </div>

          <div className="t7-catering-person">
            ♀
          </div>

        </div>

      </section>



      {/* =================================================
          CUSTOMER FAVOURITES
          
          DISPLAY ORDER 1 + 2 FROM EVERY CATEGORY
          
          CONTINUOUS HORIZONTAL AUTO SLIDER
      ================================================= */}

      {favouriteItems.length > 0 && (

        <section className="t7-favourites">

          <div className="t7-section-container">


            <div className="t7-section-heading">

              <div>

                <span className="t7-small-label">
                  LOVED BY OUR GUESTS
                </span>


                <h2>
                  Customer Favourites
                </h2>

              </div>


              <button
                type="button"
                className="t7-view-all"
                onClick={scrollToMenu}
              >

                View All

                <span>
                  →
                </span>

              </button>

            </div>

          </div>



          <div className="t7-favourites-window">

            <div className="t7-favourites-track">


              {/* =================================================
                  FIRST SET
              ================================================= */}

              {favouriteItems.map(
                (item) => (

                  <article
                    className="t7-favourite-card"
                    key={
                      `first-${item.categoryId}-${item.id}`
                    }
                  >

                    <div className="t7-favourite-image">

                      {item.image ? (

                        <img
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                        />

                      ) : (

                        <div className="t7-image-placeholder">
                          ✦
                        </div>

                      )}

                    </div>


                    <div className="t7-favourite-body">

                      <span>
                        {item.categoryName}
                      </span>


                      <h3>
                        {item.name}
                      </h3>


                      {item.description && (

                        <p>
                          {item.description}
                        </p>

                      )}


                      <div className="t7-favourite-bottom">

                        <strong>
                          ₹
                          {Number(
                            item.price
                          ).toFixed(2)}
                        </strong>


                        <button
                          type="button"
                          className="t7-favourite-add-button"
                          onClick={() =>
                            handleAddToCart(
                              item
                            )
                          }
                          aria-label={
                            `Add ${item.name} to order`
                          }
                        >

                          <span>
                            +
                          </span>

                          <span>
                            Add
                          </span>

                        </button>

                      </div>

                    </div>

                  </article>

                )
              )}



              {/* =================================================
                  SECOND SET
                  FOR SEAMLESS AUTO SLIDE
              ================================================= */}

              {favouriteItems.map(
                (item) => (

                  <article
                    className="t7-favourite-card"
                    key={
                      `second-${item.categoryId}-${item.id}`
                    }
                  >

                    <div className="t7-favourite-image">

                      {item.image ? (

                        <img
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                        />

                      ) : (

                        <div className="t7-image-placeholder">
                          ✦
                        </div>

                      )}

                    </div>


                    <div className="t7-favourite-body">

                      <span>
                        {item.categoryName}
                      </span>


                      <h3>
                        {item.name}
                      </h3>


                      {item.description && (

                        <p>
                          {item.description}
                        </p>

                      )}


                      <div className="t7-favourite-bottom">

                        <strong>
                          ₹
                          {Number(
                            item.price
                          ).toFixed(2)}
                        </strong>


                        <button
                          type="button"
                          className="t7-favourite-add-button"
                          onClick={() =>
                            handleAddToCart(
                              item
                            )
                          }
                          aria-label={
                            `Add ${item.name} to order`
                          }
                        >

                          <span>
                            +
                          </span>

                          <span>
                            Add
                          </span>

                        </button>

                      </div>

                    </div>

                  </article>

                )
              )}

            </div>

          </div>

        </section>

      )}



      {/* =================================================
          FULL MENU
      ================================================= */}

      <section
        id="t7-menu"
        className="t7-menu"
      >

        <div className="t7-section-container">


          <div className="t7-centered-heading">

            <span className="t7-small-label">
              {restaurantName}
            </span>


            <h2>
              Our Menu
            </h2>


            <div className="t7-heading-ornament">
              ❋
            </div>

          </div>



          {/* =================================================
              CATEGORY BUTTONS
          ================================================= */}

          {categories.length > 0 && (

            <div className="t7-menu-categories">

              {categories.map(
                (category) => (

                  <button
                    type="button"
                    key={
                      category.id
                    }
                    onClick={() =>
                      scrollToCategory(
                        category.id
                      )
                    }
                  >

                    {category.name}

                  </button>

                )
              )}

            </div>

          )}



          {/* =================================================
              MENU CATEGORIES
          ================================================= */}

          <div className="t7-menu-list">

            {categories.map(
              (category) => (

                <section
                  id={
                    `t7-category-${category.id}`
                  }
                  className="t7-menu-category"
                  key={
                    category.id
                  }
                >


                  <div className="t7-menu-category-heading">

                    <span>

                      {String(
                        category.display_order || 0
                      ).padStart(
                        2,
                        "0"
                      )}

                    </span>


                    <div>

                      <h3>
                        {category.name}
                      </h3>


                      {category.description && (

                        <p>
                          {category.description}
                        </p>

                      )}

                    </div>

                  </div>



                  <div className="t7-menu-items">

                    {category.items.map(
                      (item) => (

                        <article
                          className="t7-menu-item"
                          key={
                            item.id
                          }
                        >

                          <div className="t7-menu-item-image">

                            {item.image ? (

                              <img
                                src={
                                  item.image
                                }
                                alt={
                                  item.name
                                }
                                loading="lazy"
                              />

                            ) : (

                              <div className="t7-image-placeholder">
                                ✦
                              </div>

                            )}

                          </div>


                          <div className="t7-menu-item-content">

                            <div className="t7-menu-item-top">

                              <h4>
                                {item.name}
                              </h4>


                              <strong>
                                ₹
                                {Number(
                                  item.price
                                ).toFixed(2)}
                              </strong>

                            </div>


                            {item.description && (

                              <p>
                                {item.description}
                              </p>

                            )}


                            <button
                              type="button"
                              className="t7-add-button"
                              onClick={() =>
                                handleAddToCart(
                                  item
                                )
                              }
                            >
                              Add to Order
                            </button>

                          </div>

                        </article>

                      )
                    )}

                  </div>

                </section>

              )
            )}

          </div>

        </div>

      </section>



      {/* =================================================
          FLOATING CART
      ================================================= */}

      {cartCount > 0 && (

        <button
          type="button"
          className="t7-floating-cart"
          onClick={
            openCart
          }
        >

          <span className="t7-floating-count">
            {cartCount}
          </span>


          <span>
            View Cart
          </span>


          <strong>
            ₹
            {Number(
              cartTotal || 0
            ).toFixed(2)}
          </strong>

        </button>

      )}

    </main>

  );

}


export default HomeScreen;